import React, { useState } from 'react';
import { Plus, Trash2, GitBranch } from 'lucide-react';

interface ConditionStepConfigProps {
  config: any;
  onChange: (config: any) => void;
}

const CONDITION_FIELDS = [
  { value: 'email_status', label: 'Email Status', type: 'select' },
  { value: 'sms_status', label: 'SMS Status', type: 'select' },
  { value: 'call_status', label: 'Call Status', type: 'select' },
  { value: 'agreement_status', label: 'Agreement Status', type: 'select' },
  { value: 'service_type', label: 'Service Type', type: 'text' },
  { value: 'tag', label: 'Tag', type: 'text' },
  { value: 'custom_field', label: 'Custom Field', type: 'text' },
  { value: 'days_since_enrollment', label: 'Days Since Enrollment', type: 'number' },
  { value: 'days_since_last_contact', label: 'Days Since Last Contact', type: 'number' },
  { value: 'email_opens', label: 'Email Opens Count', type: 'number' },
  { value: 'link_clicks', label: 'Link Clicks Count', type: 'number' },
];

const OPERATORS = {
  text: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does not equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does not equal' },
    { value: 'greater_than', label: 'Greater than' },
    { value: 'less_than', label: 'Less than' },
    { value: 'greater_or_equal', label: 'Greater than or equal to' },
    { value: 'less_or_equal', label: 'Less than or equal to' },
  ],
  select: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does not equal' },
    { value: 'is_any_of', label: 'Is any of' },
    { value: 'is_none_of', label: 'Is none of' },
  ],
};

const STATUS_OPTIONS = {
  email_status: ['sent', 'opened', 'clicked', 'bounced', 'unsubscribed'],
  sms_status: ['sent', 'delivered', 'failed', 'opted_out'],
  call_status: ['completed', 'voicemail', 'no_answer', 'failed'],
  agreement_status: ['active', 'expired', 'cancelled', 'pending'],
};

export const ConditionStepConfig: React.FC<ConditionStepConfigProps> = ({ config, onChange }) => {
  const conditions = config.conditions || [{ field: '', operator: '', value: '' }];
  const logic = config.logic || 'all';

  const addCondition = () => {
    onChange({
      ...config,
      conditions: [...conditions, { field: '', operator: '', value: '' }],
    });
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_: any, i: number) => i !== index);
    onChange({
      ...config,
      conditions: newConditions.length > 0 ? newConditions : [{ field: '', operator: '', value: '' }],
    });
  };

  const updateCondition = (index: number, updates: any) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    onChange({
      ...config,
      conditions: newConditions,
    });
  };

  const getFieldType = (fieldValue: string) => {
    const field = CONDITION_FIELDS.find(f => f.value === fieldValue);
    return field?.type || 'text';
  };

  const getOperatorsForField = (fieldValue: string) => {
    const type = getFieldType(fieldValue);
    return OPERATORS[type as keyof typeof OPERATORS] || OPERATORS.text;
  };

  const renderValueInput = (condition: any, index: number) => {
    const fieldType = getFieldType(condition.field);
    
    // Skip value input for operators that don't need a value
    if (condition.operator === 'is_empty' || condition.operator === 'is_not_empty') {
      return null;
    }

    // Select field with predefined options
    if (fieldType === 'select' && STATUS_OPTIONS[condition.field as keyof typeof STATUS_OPTIONS]) {
      const options = STATUS_OPTIONS[condition.field as keyof typeof STATUS_OPTIONS];
      return (
        <select
          value={condition.value || ''}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select value...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>
              {opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </option>
          ))}
        </select>
      );
    }

    // Number input
    if (fieldType === 'number') {
      return (
        <input
          type="number"
          value={condition.value || ''}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          placeholder="Enter number..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      );
    }

    // Text input (default)
    return (
      <input
        type="text"
        value={condition.value || ''}
        onChange={(e) => updateCondition(index, { value: e.target.value })}
        placeholder="Enter value..."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Logic Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Match Conditions *
        </label>
        <div className="flex gap-2">
          <label className="flex-1 flex items-center justify-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="logic"
              value="all"
              checked={logic === 'all'}
              onChange={(e) => onChange({ ...config, logic: e.target.value })}
              className="mr-2 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-sm">All conditions (AND)</span>
          </label>
          <label className="flex-1 flex items-center justify-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="logic"
              value="any"
              checked={logic === 'any'}
              onChange={(e) => onChange({ ...config, logic: e.target.value })}
              className="mr-2 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-sm">Any condition (OR)</span>
          </label>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {logic === 'all' 
            ? 'Contact must match ALL conditions to proceed' 
            : 'Contact must match ANY ONE condition to proceed'}
        </p>
      </div>

      {/* Conditions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Conditions *
          </label>
          <button
            type="button"
            onClick={addCondition}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
          >
            <Plus className="w-4 h-4" />
            Add Condition
          </button>
        </div>

        <div className="space-y-3">
          {conditions.map((condition: any, index: number) => (
            <div key={index} className="relative">
              <div className="flex items-start gap-2 p-4 border rounded-lg bg-gray-50">
                {/* Condition Number Badge */}
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-xs font-medium mt-2">
                  {index + 1}
                </div>

                {/* Condition Fields */}
                <div className="flex-1 grid grid-cols-3 gap-2">
                  {/* Field Selection */}
                  <div>
                    <select
                      value={condition.field || ''}
                      onChange={(e) => updateCondition(index, { field: e.target.value, operator: '', value: '' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="">Select field...</option>
                      {CONDITION_FIELDS.map(field => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Operator Selection */}
                  <div>
                    <select
                      value={condition.operator || ''}
                      onChange={(e) => updateCondition(index, { operator: e.target.value })}
                      disabled={!condition.field}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100"
                    >
                      <option value="">Select operator...</option>
                      {condition.field && getOperatorsForField(condition.field).map((op: any) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Value Input */}
                  <div>
                    {condition.operator && renderValueInput(condition, index)}
                  </div>
                </div>

                {/* Remove Button */}
                {conditions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCondition(index)}
                    className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg mt-1"
                    title="Remove condition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Logic connector between conditions */}
              {index < conditions.length - 1 && (
                <div className="flex items-center justify-center my-2">
                  <span className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded">
                    {logic === 'all' ? 'AND' : 'OR'}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Branch Paths */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Branch Paths</h4>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
            <GitBranch className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-sm text-green-900">If conditions match (Yes)</div>
              <div className="text-xs text-green-700 mt-1">
                Continue to the next step in the workflow
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
            <GitBranch className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-sm text-red-900">If conditions don't match (No)</div>
              <div className="text-xs text-red-700 mt-1">
                <label className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={config.exit_workflow_on_no || false}
                    onChange={(e) => onChange({ ...config, exit_workflow_on_no: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2">Exit workflow (stop execution)</span>
                </label>
                {!config.exit_workflow_on_no && (
                  <p className="mt-2 text-xs">
                    Contact will skip to the next condition step or exit if none exists
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Condition Tips</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Use AND logic when all conditions must be true</li>
          <li>• Use OR logic when any single condition being true is sufficient</li>
          <li>• Conditions are evaluated in real-time when the contact reaches this step</li>
          <li>• You can chain multiple condition steps for complex logic</li>
          <li>• Test your conditions with a small segment before deploying to all contacts</li>
        </ul>
      </div>
    </div>
  );
};
